export interface PlaceResult {
  formatted_address?: string;
  address_components?: {
    types: string[];
    long_name: string;
    short_name: string;
  }[];
}

export interface PlacesListener {
  remove: () => void;
}

interface MapsRuntime {
  importLibrary?: (name: string) => Promise<unknown>;
  places?: {
    Autocomplete: new (
      input: HTMLInputElement,
      options: object
    ) => {
      getPlace: () => PlaceResult;
      addListener: (name: string, callback: () => void) => PlacesListener;
    };
  };
}

export const getGoogleMaps = () =>
  (window as Window & { google?: { maps?: MapsRuntime } }).google?.maps;

let pending: Promise<void> | undefined;

const placesReady = () => !!getGoogleMaps()?.places?.Autocomplete;

export const loadPlaces = (
  apiKey: string,
  language = 'en',
  libraries = ['places'],
  scriptBaseUrl = 'https://maps.googleapis.com/maps/api/js'
): Promise<void> => {
  if (placesReady()) {
    return Promise.resolve();
  }
  if (pending) {
    return pending;
  }
  if (!apiKey) {
    return Promise.reject(new Error('Address suggestions unavailable'));
  }

  const request = new Promise<void>((resolve, reject) => {
    const existing = Array.from(document.scripts).find((script) =>
      script.src.startsWith(scriptBaseUrl)
    );
    const script = existing ?? document.createElement('script');
    const owned = !existing || script.dataset.clixPlaces === 'true';
    let finished = false;
    let importing = false;
    const cleanup = () => {
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      script.removeEventListener('error', fail);
      script.removeEventListener('load', check);
    };
    const fail = () => {
      if (finished) {
        return;
      }
      finished = true;
      cleanup();
      if (owned) {
        script.remove();
      }
      reject(new Error('Address suggestions unavailable'));
    };
    const check = () => {
      if (finished) {
        return;
      }
      if (placesReady()) {
        finished = true;
        cleanup();
        resolve();
      } else if (!importing && getGoogleMaps()?.importLibrary) {
        importing = true;
        getGoogleMaps()!.importLibrary!('places').then(check).catch(fail);
      }
    };
    const poll = window.setInterval(check, 100);
    const timeout = window.setTimeout(fail, 10000);
    script.addEventListener('error', fail);
    script.addEventListener('load', check);
    if (!existing && !getGoogleMaps()?.importLibrary) {
      const url = new URL(scriptBaseUrl);
      url.searchParams.set('key', apiKey);
      url.searchParams.set('language', language);
      url.searchParams.set(
        'libraries',
        Array.from(new Set([...libraries, 'places'])).join(',')
      );
      script.src = url.toString();
      script.async = true;
      script.dataset.clixPlaces = 'true';
      document.head.appendChild(script);
    }
    check();
  });
  pending = request;
  request.then(
    () => {
      if (pending === request) {
        pending = undefined;
      }
    },
    () => {
      if (pending === request) {
        pending = undefined;
      }
    }
  );
  return request;
};
